import { useState } from 'react';

import ReCAPTCHA from 'react-google-recaptcha';
import { useForm } from 'react-hook-form';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { TokenResponse, useGoogleLogin } from '@react-oauth/google';
import { z } from 'zod';

import Button from 'components/Button/Button';
import Input from 'components/Input/Input';
import Typography from 'components/Typography/Typography';
import { Form, FormControl, FormField, FormItem } from 'components/ui';
import { Spinner } from 'components/ui/spinner';

import {
  getFieldState,
  getPasswordVisibilityType,
  getRecaptchaSiteKey,
  getRevealPasswordIconName
} from '../../../../utils/recaptchaFormHelpers';
import { SIGN_UP_FORM_SCHEMA } from '../utils/constants';
import { useSignUp } from '../utils/useSignUp';

const SignUpForm = () => {
  const {
    recaptchaRef,
    isLoading,
    handleGoogleRegistration,
    handleRegistration
  } = useSignUp();

  const [showPassword, setShowPassword] = useState(false);

  const googleLogin = useGoogleLogin({
    onSuccess: (response: TokenResponse) => {
      handleGoogleRegistration({ loginToken: response.access_token });
    },
    onError: () => {
      console.error('Google login failed');
    }
  });

  const form = useForm<z.infer<typeof SIGN_UP_FORM_SCHEMA>>({
    resolver: zodResolver(SIGN_UP_FORM_SCHEMA),
    mode: 'onChange',
    defaultValues: {
      name: '',
      email: '',
      password: ''
    }
  });

  const {
    formState: { errors, isValid }
  } = form;

  const handleFormSubmit = () => {
    form.handleSubmit(handleRegistration)();
  };

  return (
    <div className="w-full">
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={getRecaptchaSiteKey()}
        size="invisible"
        className="hidden"
      />
      {isLoading ? (
        <div className="flex justify-center items-center h-full">
          <Spinner className="size-12" />
        </div>
      ) : (
        <Form {...form}>
          <Button
            className="w-full shadow-sm"
            disabled={isLoading}
            variant="outline"
            onClick={() => googleLogin()}
            leftIcon={
              <img
                className="w-4 h-4"
                src="https://media.appcharge.com/defaults/google_icon.svg"
                alt="google icon"
              />
            }
          >
            Continue with Google
          </Button>
          <div className="flex items-center justify-center gap-x-5 my-[22px] w-full text-p-xs text-base-mutedForeground">
            <hr className="w-full h-[1px] bg-base-sidebarBorder" />
            OR
            <hr className="w-full h-[1px] bg-base-sidebarBorder" />
          </div>
          <form
            onSubmit={form.handleSubmit(handleRegistration)}
            className="flex flex-col gap-y-4 w-full"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      isRequired
                      label="Full Name"
                      placeholder="Full Name"
                      state={getFieldState(errors.name?.message, !!errors.name)}
                      description={errors.name?.message}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      isRequired
                      type="email"
                      label="Email"
                      placeholder="Email"
                      state={getFieldState(
                        errors.email?.message,
                        !!errors.email
                      )}
                      description={errors.email?.message}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      rightIcon={
                        <FontAwesomeIcon
                          onClick={() => setShowPassword((prev) => !prev)}
                          icon={getRevealPasswordIconName(showPassword)}
                        />
                      }
                      type={getPasswordVisibilityType(showPassword)}
                      isRequired
                      label="Password"
                      placeholder="Password"
                      state={getFieldState(
                        errors.password?.message,
                        !!errors.password
                      )}
                      description={errors.password?.message}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <Button
              disabled={!isValid}
              onClick={handleFormSubmit}
              className="w-full"
            >
              Sign Up
            </Button>
          </form>
          <Typography className="text-p-s text-base-foreground mt-9 text-center">
            Already signed up?{' '}
            <a href="/login" className="font-bold underline">
              Log In
            </a>
          </Typography>
        </Form>
      )}
    </div>
  );
};

export default SignUpForm;
